import {
  confirmSignIn,
  fetchAuthSession,
  fetchUserAttributes,
  FetchUserAttributesOutput,
  getCurrentUser,
  GetCurrentUserOutput,
  JWT,
  signIn,
  signOut,
} from 'aws-amplify/auth';
import { SignInResponse } from './types';

class AuthenticationService {
  async signInFirstStep(
    email: string,
    password: string
  ): Promise<SignInResponse> {
    try {
      const result = await signIn({ username: email, password });

      return {
        isSignedIn: result.isSignedIn,
        nextStep: result.nextStep,
        status: 200,
        message: 'Primeira etapa do login realizada com sucesso.',
      };
    } catch (err: unknown) {
      return {
        isSignedIn: false,
        nextStep: 'NONE',
        status: 401,
        message: err instanceof Error ? err.message : 'Unknown error',
      };
    }
  }

  async confirmSignInWithNewPasswordRequired(
    password: string
  ): Promise<SignInResponse> {
    try {
      const result = await confirmSignIn({
        challengeResponse: password,
      });
      return {
        isSignedIn: result.isSignedIn,
        nextStep: result.nextStep,
        status: 200,
        message: 'Etapa do login realizada com sucesso',
      };
    } catch (err: unknown) {
      return {
        isSignedIn: false,
        nextStep: 'NONE',
        status: 401,
        message: err instanceof Error ? err.message : 'Unknown error',
      };
    }
  }

  async confirmSignInWithEmailCode(code: string): Promise<SignInResponse> {
    try {
      const result = await confirmSignIn({
        challengeResponse: code,
      });
      return {
        isSignedIn: result.isSignedIn,
        nextStep: result.nextStep,
        status: 200,
        message: 'Etapa do login realizada com sucesso',
      };
    } catch (err: unknown) {
      return {
        isSignedIn: false,
        nextStep: 'NONE',
        status: 401,
        message: err instanceof Error ? err.message : 'Unknown error',
      };
    }
  }

  async getCurrentUser(): Promise<{
    user: GetCurrentUserOutput;
    attributes: FetchUserAttributesOutput;
  } | null> {
    try {
      const user = await getCurrentUser();
      const attributes = await fetchUserAttributes();

      return { user, attributes };
    } catch {
      return null;
    }
  }

  async signOutCurrentUser(): Promise<void> {
    await signOut();
  }

  async getAuthSession(): Promise<JWT | null> {
    try {
      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken || null;
      return accessToken;
    } catch {
      return null;
    }
  }

  /**
   * Confirma o primeiro acesso no Portal.Api, atualizando o status do usuário
   * no InsureMO de FORCE_CHANGE_PASSWORD para ACTIVE.
   * Fire-and-forget: não bloqueia o fluxo em caso de erro.
   */
  async confirmFirstAccess(): Promise<void> {
    try {
      const token = await this.getAuthSession();
      if (!token) {
        console.log('[confirmFirstAccess] Sem token, ignorando chamada');
        return;
      }

      const baseUrl =
        import.meta.env.VITE_API_URL || 'http://localhost:8080';
      const url = `${baseUrl}/portal/v1/identity/auth/confirm-first-access`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token.toString()}`,
        },
      });

      if (response.ok) {
        console.log('[confirmFirstAccess] Endpoint chamado com sucesso:', response.status);
      } else {
        console.warn(
          '[confirmFirstAccess] Falha ao confirmar primeiro acesso:',
          response.status
        );
      }
    } catch (error) {
      console.warn('[confirmFirstAccess] Erro ao confirmar primeiro acesso:', error);
    }
  }
}

export const authenticationService = new AuthenticationService();
export default authenticationService;
